import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import {
  Loader2, Dog, Users, User, Shield, Edit2, Check, X,
  Globe, Camera, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportCaptainPage() {
  const { token } = useParams();
  const { language, setLanguage } = useLanguage();
  const [team, setTeam] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('team');
  const [editingDog, setEditingDog] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/sport/captain/verify?token=${token}`);
        setTeam(res.data.team);
        setPermissions(res.data.permissions);
      } catch (err) {
        setError(err.response?.data?.detail || (language === 'pl' ? 'Nieprawidlowy lub wygasly link' : 'Invalid or expired link'));
      } finally {
        setLoading(false);
      }
    };
    verify();
  }, [token, language]);

  useEffect(() => {
    if (!team || !permissions.includes('assign_clusters')) return;
    const fetchClusters = async () => {
      try {
        const res = await axios.get(`${API}/sport/captain/clusters?token=${token}&status=unassigned`);
        setClusters(res.data.clusters);
      } catch (err) {
        console.error('Failed to load clusters:', err);
      }
    };
    fetchClusters();
  }, [team, token, permissions]);

  const saveDogName = async (dogId) => {
    try {
      await axios.put(`${API}/sport/captain/dogs/${dogId}?token=${token}`, { name: editName });
      setTeam(prev => ({
        ...prev,
        dogs: prev.dogs.map(d => d.id === dogId ? { ...d, name: editName } : d),
      }));
      toast.success(language === 'pl' ? 'Zapisano' : 'Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setEditingDog(null);
  };

  const assignCluster = async (clusterId, dogId) => {
    try {
      await axios.post(`${API}/sport/captain/clusters/${clusterId}/assign?token=${token}`, { dog_id: dogId });
      setClusters(prev => prev.filter(c => c.id !== clusterId));
      toast.success(language === 'pl' ? 'Przypisano' : 'Assigned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">
              {language === 'pl' ? 'Blad dostepu' : 'Access Error'}
            </h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-lg font-bold">
                {language === 'pl' ? 'Portal Kapitana' : 'Captain Portal'}
              </h1>
              <p className="text-xs text-muted-foreground">{team?.name}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}>
            <Globe className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <section className="border-b border-border px-6">
        <div className="max-w-5xl mx-auto flex gap-1">
          {[
            { key: 'team', icon: Users, pl: 'Druzyna', en: 'Team' },
            { key: 'clusters', icon: Camera, pl: 'Klastry', en: 'Clusters', badge: clusters.length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {language === 'pl' ? t.pl : t.en}
              {t.badge > 0 && (
                <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Team Tab */}
        {tab === 'team' && team && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Team info */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{team.name}</CardTitle>
              </CardHeader>
              <CardContent>
                {team.club && <p className="text-muted-foreground mb-2">{team.club}</p>}
                <p className="text-sm text-muted-foreground">
                  {(team.dogs || []).length} {language === 'pl' ? 'psow' : 'dogs'} /{' '}
                  {(team.competitors || []).length} {language === 'pl' ? 'zawodnikow' : 'competitors'}
                </p>
              </CardContent>
            </Card>

            {/* Dogs */}
            <h3 className="text-lg font-semibold mb-3">{language === 'pl' ? 'Psy' : 'Dogs'}</h3>
            <div className="space-y-2 mb-8">
              {(team.dogs || []).map(dog => (
                <Card key={dog.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Dog className="w-6 h-6 text-muted-foreground" />
                      {editingDog === dog.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            className="h-8 w-48"
                            autoFocus
                          />
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => saveDogName(dog.id)}>
                            <Check className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingDog(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <p className="font-medium">{dog.name || `#${dog.id.slice(0, 6)}`}</p>
                          {dog.breed && <p className="text-xs text-muted-foreground">{dog.breed}</p>}
                        </div>
                      )}
                    </div>
                    {permissions.includes('edit_roster') && editingDog !== dog.id && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => { setEditingDog(dog.id); setEditName(dog.name || ''); }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Competitors */}
            <h3 className="text-lg font-semibold mb-3">{language === 'pl' ? 'Zawodnicy' : 'Competitors'}</h3>
            <div className="space-y-2">
              {(team.competitors || []).map(comp => (
                <Card key={comp.id}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <User className="w-6 h-6 text-muted-foreground" />
                    <p className="font-medium">{comp.first_name} {comp.last_name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Clusters Tab */}
        {tab === 'clusters' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p className="text-muted-foreground mb-6">
              {language === 'pl'
                ? 'Przypisz nierozpoznane klastry do psow z Twojej druzyny.'
                : 'Assign unrecognized clusters to dogs from your team.'}
            </p>

            {clusters.length === 0 ? (
              <div className="text-center py-16">
                <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="text-muted-foreground">
                  {language === 'pl' ? 'Wszystkie klastry przypisane' : 'All clusters assigned'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {clusters.map(cluster => (
                  <Card key={cluster.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Sample photo */}
                        <div className="w-20 h-20 bg-secondary rounded-lg flex-shrink-0 overflow-hidden">
                          {cluster.sample_photo?.variants?.thumbnail ? (
                            <img
                              src={cluster.sample_photo.variants.thumbnail}
                              alt="sample"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Camera className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <p className="text-sm font-medium mb-1">
                            {cluster.event_name} — {cluster.cluster_type}
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            {language === 'pl' ? 'Przypisz do psa:' : 'Assign to dog:'}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {(team?.dogs || []).map(dog => (
                              <Button
                                key={dog.id}
                                size="sm"
                                variant="outline"
                                onClick={() => assignCluster(cluster.id, dog.id)}
                              >
                                <Dog className="w-3 h-3 mr-1" />
                                {dog.name || `#${dog.id.slice(0, 6)}`}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
